import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Plus, Minus, PackageCheck, Loader2, Truck, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { fmtMoney, fmtDateDow } from "@/components/status";

export default function Cart() {
  const navigate = useNavigate();
  const { list, total, count, add, setQty, clear } = useCart();
  const [placedBy, setPlacedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [options, setOptions] = useState(null);
  const [selected, setSelected] = useState(null); // {delivery_date, day_key}
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.deliveryOptions().then(setOptions).catch(() => {}); }, []);

  const step = () => 1; // cartons increment 1 at a time
  const weeks = useMemo(() => (options?.weeks || []).filter((w) => (w.slots || []).length), [options]);
  const allSlots = useMemo(() => weeks.flatMap((w) => w.slots || []), [weeks]);
  const firstOpen = allSlots.find((s) => s.is_open);
  const hasSchedule = allSlots.length > 0;
  const thisWeekClosed = !!options && (options.weeks?.[0]?.slots?.length ? !options.weeks[0].slots.some((s) => s.is_open) : false);

  useEffect(() => {
    if (firstOpen && !selected) setSelected({ delivery_date: firstOpen.delivery_date, day_key: firstOpen.day_key });
  }, [firstOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!list.length) return toast.error("Your cart is empty");
    if (!placedBy.trim()) return toast.error("Please enter who is placing this order");
    if (hasSchedule && !selected) return toast.error("Please choose a delivery day");
    setBusy(true);
    try {
      await api.createOrder({
        items: list.map((i) => ({ flower_id: i.flower.id, name: i.flower.name, price: i.flower.price, unit: i.flower.unit || "carton", pack_size: i.flower.pack_size, pack_unit: i.flower.pack_unit, quantity: i.qty, image_url: i.flower.image_url, is_special: !!i.flower.is_special, special_id: i.flower.special_id || undefined })),
        save_as_standing: false, notes: "", placed_by_name: placedBy.trim(), delivery_notes: notes.trim(),
        target_delivery_date: selected?.delivery_date || undefined,
        target_delivery_day: selected?.day_key || undefined,
      });
      toast.success("Order placed!");
      clear();
      navigate("/store/orders");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate("/store/catalogue")} className="mb-3 flex items-center gap-1 text-sm text-[#EBBAD1]"><ArrowLeft className="h-4 w-4" /> Back to catalogue</button>
      <h1 className="mb-4 font-serif text-2xl font-extrabold">Your Cart</h1>

      {list.length === 0 ? (
        <div className="rounded-2xl bg-white/10 py-16 text-center text-sm text-white/85">Your cart is empty. Add flowers from the catalogue.</div>
      ) : (
        <div className="space-y-3">
          {list.map((i) => (
            <div key={i.flower.id} data-testid={`cart-item-${i.flower.id}`} className="flex items-center gap-3 rounded-2xl bg-white/10 p-3">
              <img src={i.flower.image_url} alt={i.flower.name} className="h-14 w-14 shrink-0 rounded-lg bg-white object-contain" />
              <div className="min-w-0 flex-1"><div className="truncate font-semibold">{i.flower.name}</div><div className="text-xs text-white/85">{fmtMoney(i.flower.price)} each</div></div>
              <div className="flex items-center gap-1.5 rounded-full bg-black/20 p-1">
                <button data-testid={`cart-minus-${i.flower.id}`} onClick={() => add(i.flower, -step())} className="grid h-7 w-7 place-items-center rounded-full bg-[#EBBAD1] text-[#28282E]"><Minus className="h-3.5 w-3.5" /></button>
                <span className="min-w-[1.5rem] text-center text-sm font-bold">{i.qty}</span>
                <button data-testid={`cart-plus-${i.flower.id}`} onClick={() => add(i.flower, step())} className="grid h-7 w-7 place-items-center rounded-full bg-[#EBBAD1] text-[#28282E]"><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <button data-testid={`cart-remove-${i.flower.id}`} onClick={() => setQty(i.flower, 0)} className="text-white/78 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}

          {hasSchedule ? (
            <div className="rounded-2xl bg-white/10 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm text-white/90"><Truck className="h-4 w-4 text-[#EBBAD1]" /> Choose a delivery day</div>
              {thisWeekClosed && (
                <div data-testid="cart-cutoff-warning" className="mb-3 flex items-start gap-2 rounded-xl bg-[#28282E]/50 p-3 text-xs text-[#EBBAD1]">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>This week's cutoff has passed. The earliest delivery available is <b>{firstOpen ? fmtDateDow(firstOpen.delivery_date) : "a later week"}</b>.</span>
                </div>
              )}
              <div className="space-y-3">
                {weeks.map((w) => (
                  <div key={w.week_commencing}>
                    <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-white/78">{w.week_label}</div>
                    <div className="flex flex-wrap gap-2">
                      {w.slots.map((s) => {
                        const isSel = selected?.delivery_date === s.delivery_date && selected?.day_key === s.day_key;
                        return (
                          <button key={s.delivery_date + s.day_key} data-testid={`cart-slot-${s.delivery_date}`} disabled={!s.is_open}
                            onClick={() => setSelected({ delivery_date: s.delivery_date, day_key: s.day_key })}
                            className={`rounded-xl px-3 py-2 text-xs font-bold transition ${isSel ? "bg-[#EBBAD1] text-[#28282E]" : s.is_open ? "bg-black/20 text-white/85 hover:bg-black/30" : "cursor-not-allowed bg-black/10 text-white/65"}`}>
                            {fmtDateDow(s.delivery_date)}{!s.is_open ? ` · ${s.is_locked_by_production ? "locked" : "closed"}` : ""}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl bg-white/10 p-4 text-xs text-white/85">Delivery will be scheduled by Hasfarm for the next available window.</div>
          )}

          <input data-testid="cart-placedby" value={placedBy} onChange={(e) => setPlacedBy(e.target.value)} placeholder="Placed by (your name)"
            className="w-full rounded-2xl border border-white/20 bg-black/15 px-4 py-3 text-sm text-white placeholder:text-white/78 outline-none focus:border-[#EBBAD1]" />
          <textarea data-testid="cart-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Order notes (optional)"
            className="min-h-[64px] w-full resize-none rounded-2xl border border-white/20 bg-black/15 px-4 py-3 text-sm text-white placeholder:text-white/78 outline-none focus:border-[#EBBAD1]" />

          <div className="flex items-center justify-between px-1 text-sm text-white/90">
            <span>{count} {count === 1 ? "item" : "items"}</span>
            <span data-testid="cart-total" className="font-serif text-2xl font-extrabold text-white">{fmtMoney(total)}</span>
          </div>
          <button data-testid="cart-submit" onClick={submit} disabled={busy || (hasSchedule && !selected)} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#28282E] py-3.5 text-base font-bold text-white hover:bg-[#1F1F24] disabled:opacity-70">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <PackageCheck className="h-5 w-5" />} Confirm Order
          </button>
        </div>
      )}
    </div>
  );
}
